import type { DocBlock, DocField, FieldDiff, SablonSurum, UploadedTemplateFile, VariableMapping } from '../types'

export function emptySistemSurum(sablonId: string, version: number): SablonSurum {
  const pageId = `pg-${crypto.randomUUID().slice(0, 8)}`
  const secId = `sec-${crypto.randomUUID().slice(0, 8)}`
  return {
    id: `srv-${crypto.randomUUID().slice(0, 8)}`,
    sablonId,
    version,
    status: 'taslak',
    changeReason: '',
    method: 'sistem',
    pages: [{ id: pageId, title: '1. Sayfa', order: 0 }],
    sections: [{ id: secId, title: 'Kapak / Başlık', pageId, order: 0 }],
    fields: [],
    blocksBySection: {
      [secId]: [
        {
          id: `b-${crypto.randomUUID().slice(0, 8)}`,
          kind: 'heading',
          text: 'Yeni Belge',
          level: 1,
        },
        {
          id: `b-${crypto.randomUUID().slice(0, 8)}`,
          kind: 'richtext',
          html: '<p>İçeriği buraya yazın. Sağ panellerden değişken veya alan ekleyebilirsiniz.</p>',
        },
      ],
    },
    createdAt: new Date().toISOString(),
    editorFont: 'Arial, Helvetica, sans-serif',
    editorFontSize: 11,
    lineHeight: 1.5,
    margins: { top: 20, right: 18, bottom: 20, left: 18 },
  }
}

export function cloneSurumAsDraft(source: SablonSurum, nextVersion: number): SablonSurum {
  const json = structuredClone(source)
  return {
    ...json,
    id: `srv-${crypto.randomUUID().slice(0, 8)}`,
    version: nextVersion,
    status: 'taslak',
    changeReason: '',
    createdAt: new Date().toISOString(),
    publishedAt: undefined,
  }
}

export function validateForPublish(surum: SablonSurum): string[] {
  const errors: string[] = []
  if (surum.method === 'docx' || surum.method === 'pdf') {
    if (!surum.uploadedFile) errors.push('file_missing')
  }
  if (surum.method === 'sistem' && surum.fields.length === 0 && surum.sections.length === 0) {
    errors.push('empty_template')
  }
  const undefinedMappings = (surum.variableMappings ?? []).filter((m) => m.status === 'tanimsiz')
  if (undefinedMappings.length) errors.push('undefined_vars')
  const requiredUnconfigured = surum.fields.filter((f) => f.required && f.source === 'otomatik' && !f.variable)
  if (requiredUnconfigured.length) errors.push('required_mapping')
  return errors
}

export function computeFieldDiff(oldSurum: SablonSurum, newSurum: SablonSurum): FieldDiff[] {
  const oldMap = new Map(oldSurum.fields.map((f) => [f.key, f]))
  const newMap = new Map(newSurum.fields.map((f) => [f.key, f]))
  const keys = new Set([...oldMap.keys(), ...newMap.keys()])
  const diffs: FieldDiff[] = []
  for (const key of keys) {
    const a = oldMap.get(key)
    const b = newMap.get(key)
    if (a && !b) diffs.push({ key, label: a.label, kind: 'removed' })
    else if (!a && b) diffs.push({ key, label: b.label, kind: 'added' })
    else if (a && b) {
      const changed = a.label !== b.label || a.type !== b.type || a.source !== b.source || a.required !== b.required || a.variable !== b.variable
      diffs.push({ key, label: b.label, kind: changed ? 'changed' : 'unchanged' })
    }
  }
  return diffs
}

export function buildMappingsFromDemo(vars: string[], fields: DocField[]): VariableMapping[] {
  return vars.map((v) => {
    const match = fields.find((f) => f.variable === v || f.key === v.split('.').pop())
    if (match) return { templateVar: v, mappedFieldKey: match.key, status: 'eslesti' as const }
    if (v.includes('tanimsiz') || v.startsWith('custom.')) {
      return { templateVar: v, mappedFieldKey: null, status: 'tanimsiz' as const, note: `{{${v}}} personel alanlarıyla eşleştirilemedi.` }
    }
    return { templateVar: v, mappedFieldKey: null, status: 'manuel' as const }
  })
}

export function applyUploadedFile(surum: SablonSurum, file: UploadedTemplateFile): SablonSurum {
  if (file.kind === 'pdf') {
    return { ...surum, method: 'pdf', uploadedFile: file, demoVariables: undefined, variableMappings: [] }
  }
  const demoVariables = surum.demoVariables ?? []
  return {
    ...surum,
    method: 'docx',
    uploadedFile: file,
    demoVariables,
    variableMappings: buildMappingsFromDemo(demoVariables, surum.fields),
  }
}

export function upsertField(surum: SablonSurum, field: DocField): SablonSurum {
  const exists = surum.fields.some((f) => f.id === field.id)
  const fields = exists ? surum.fields.map((f) => (f.id === field.id ? field : f)) : [...surum.fields, field]
  return { ...surum, fields }
}

export function removeField(surum: SablonSurum, fieldId: string): SablonSurum {
  const blocksBySection: Record<string, DocBlock[]> = {}
  for (const [secId, blocks] of Object.entries(surum.blocksBySection)) {
    blocksBySection[secId] = blocks.filter((b) => !((b.kind === 'field' || b.kind === 'signature') && b.fieldId === fieldId))
  }
  return { ...surum, fields: surum.fields.filter((f) => f.id !== fieldId), blocksBySection }
}

export function slugifyKey(label: string): string {
  return (
    label
      .toLocaleLowerCase('tr')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 40) || 'alan'
  )
}

export function stripPreview(surum: SablonSurum): SablonSurum {
  if (!surum.uploadedFile?.previewUrl) return surum
  const file = { ...surum.uploadedFile }
  delete file.previewUrl
  return { ...surum, uploadedFile: file }
}
